import { t } from "@hakili/i18n";
import { Field } from "@hakili/ui";

import type { MakeReference, VehicleOptions } from "../session/useSession.js";
import type { EquipmentReference } from "./equipment.js";

/**
 * `P0-DLW-032`, round F4: **the car's fields, written ONCE.**
 *
 * The add panel and the car's own « Modifier » ask for the same thing, and the
 * architect's order says so in terms: *« Modifier » opening the panel's own
 * form on the car (one form component, the reference read-only after
 * publication)*. Two copies would disagree the first time a field moved, and
 * the one that disagreed would be the edit , the screen nobody demonstrates.
 *
 * ## What the founder decided, on 7 September, reading the add form
 *
 * - **« Marque » and « Modèle » are LISTS**, the model depending on the make,
 *   served from the reference the api maintains, each carrying « Autre » with a
 *   text box so a missing name never blocks a listing.
 * - **« Boîte » and « Carburant » are lists** over the values the api already
 *   knows, served like the photograph views: key and label held apart.
 * - **« Année », « Kilométrage », « Prix », « Couleur » and « Places » stay
 *   free.** They are numbers and a word; a list of colours would be this
 *   platform deciding what colour a car may be.
 * - **« Ville » stays a list** (`R64`, rule one) and « Quartier » as it is.
 * - **The plate is OPTIONAL**, in Mali some dealerships sell cars registered
 *   and some do not. The field says so, because a field that looks required and
 *   is not is a field people invent values for.
 *
 * ## The price is not here, and that is the whole shape of round F4
 *
 * It is on the add form because a car is listed with one, and it is NOT on the
 * edit form because it has its own door, its own append-only history and its
 * own journal line , `DECISION ADDED 14` computes the verification fee from the
 * price in force when an advance is taken, and an edit that moved the price
 * without a history row would break the only record of what that was.
 */

/**
 * The vocabulary before the door has answered.
 *
 * Empty, and never a guess: a select drawn from a guessed list is a select
 * whose options change under the person reading them, and the panel's own
 * views learned that in `F2.3`.
 */
export const NO_MAKES: MakeReference = { makes: [], otherMake: "autre" };
export const NO_OPTIONS: VehicleOptions = { gearboxes: [], fuels: [] };

/** The reference and the options, as one bundle, so a caller passes one prop. */
export interface VehicleVocabulary {
  readonly makes: MakeReference;
  readonly options: VehicleOptions;
  readonly equipment: EquipmentReference;
  readonly cities: readonly { readonly id: string; readonly name: string }[];
  readonly places: readonly { readonly code: string; readonly label: string }[];
}

export interface VehicleFieldsProps {
  readonly values: Readonly<Record<string, string>>;
  readonly onChange: (key: string, value: string) => void;
  /**
   * The door's own refusal, shown BESIDE the field it names.
   *
   * `4.5.5` puts the field in `details`; a screen that printed only a status,
   * or printed the sentence at the top of a long form, makes a person hunt for
   * what to change. Absent when nothing was refused.
   */
  readonly refusal?: { readonly field: string; readonly message: string } | undefined;
  readonly vocabulary: VehicleVocabulary;
  readonly chosenEquipment: readonly string[];
  readonly onToggleEquipment: (entry: string) => void;
  /**
   * `create` asks for the price; `edit` does not, and the difference is a door
   * and not a preference.
   */
  readonly mode: "create" | "edit";
  /**
   * The founder's decision: the reference is proposed and editable, **and
   * frozen once the car is published**, because from that moment it is the
   * number a buyer, a cabinet and an invoice all quote.
   */
  readonly referenceLocked?: boolean;
}

/**
 * `round F5`, `F5.1`: **where a car is, as ONE contract.**
 *
 * The founder's walk of 8 September: he edited a car from Ségou to Bamako and
 * the door answered `400`, because the form let « Enregistrer » be pressed with
 * the quartier unchosen.
 *
 * **The mechanism in one sentence.** Changing the city empties the quartier ,
 * which is right, a quartier chosen for Ségou means nothing in Bamako , and
 * nothing then stopped the save, so the door was asked to resolve a place with
 * no quartier and refused, correctly, in a sentence the screen showed nowhere
 * near the field.
 *
 * It is the same shape in both directions and the refusal has two different
 * authors, which is why one guard is worth more than two patches:
 *
 * - **into Bamako**, `resolvePlaceIn` needs the quartier to pick one of the
 *   sixty-six that ARE the routing map, and refuses without one;
 * - **out of Bamako**, rule 3 ignores the quartier entirely and returns the
 *   city's own zone , so the resolver is happy and the AGGREGATE refuses
 *   instead, on its required set. Same failure, second author.
 *
 * So the rule lives here, once, and both forms obey it by using this component:
 * the quartier depends on the city exactly as the model depends on the make ,
 * it resets when the city changes, it takes the city's SHAPE (Bamako's list of
 * sixty-six, a text box everywhere else), and a form cannot be submitted until
 * it is filled. **A stale quartier can never be sent with a new city**, because
 * the change that sets the city is the change that clears it.
 */
export function placeIsComplete(
  values: Readonly<Record<string, string>>,
  places: readonly { readonly code: string; readonly label: string }[],
): boolean {
  const city = (values["city"] ?? "").trim();
  if (city === "") return false;
  // The quartier is required in both shapes: inside Bamako it chooses the zone,
  // and outside it is the address the tour sheet carries. The aggregate demands
  // it either way , « Le quartier est obligatoire. » , so a screen that let it
  // through would only be moving the refusal later.
  if ((values["district"] ?? "").trim() === "") return false;
  // Inside a city that offers a list, what is held must be ON the list: a
  // quartier typed for another city, or left from one, is not a zone here.
  if (places.length > 1) {
    return places.some((place) => place.label === values["district"]);
  }
  return true;
}

/** The four the phone should offer digits for. */
const NUMERIC = new Set(["year", "mileageKm", "priceAmount", "seats"]);

const INPUT = "w-full rounded-xl border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-300";

/**
 * The free fields, in the drawing's order. `make`, `model`, `gearbox` and
 * `fuel` are NOT here: they are lists now, rendered above with their « Autre ».
 */
const FREE_FIELDS: readonly {
  readonly key: string;
  readonly cle: string;
  readonly exemple: string;
}[] = [
  { key: "year", cle: "dealer.vehicules.annee", exemple: "dealer.vehicules.anneeExemple" },
  {
    key: "mileageKm",
    cle: "dealer.vehicules.kilometrage",
    exemple: "dealer.vehicules.kilometrageExemple",
  },
  { key: "colour", cle: "dealer.vehicules.couleur", exemple: "dealer.vehicules.couleurExemple" },
  { key: "seats", cle: "dealer.vehicules.places", exemple: "dealer.vehicules.placesExemple" },
];

/**
 * A list with an escape.
 *
 * The escape is not a row in the reference and never becomes one: choosing it
 * opens a text box, and what is typed is what the car carries. That is the
 * opposite of a photograph's « autre », which IS a stored value, and the two
 * are different on purpose , a view must be one of twelve, a make must not be
 * one of thirty.
 */
function ListWithOther({
  id,
  label,
  value,
  options,
  otherKey,
  onChange,
  placeholder,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly options: readonly { readonly key: string; readonly label: string }[];
  readonly otherKey: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
}): React.JSX.Element {
  // A value the list does not carry IS the « Autre » case, and the box holds
  // it. This is what makes an edit of a car listed as « Sonacos » open with
  // « Sonacos » in the box rather than silently snapping to the first make.
  const known = options.some((option) => option.key === value);
  const typing = value !== "" && !known;
  return (
    <div>
      <Field label={label} htmlFor={id}>
        <select
          id={id}
          value={typing ? otherKey : value}
          onChange={(event) => {
            onChange(event.target.value === otherKey ? " " : event.target.value);
          }}
          className={INPUT}
          data-testid={id}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
          <option value={otherKey}>{t("dealer.vehicules.autre")}</option>
        </select>
      </Field>
      {typing ? (
        <input
          value={value.trim()}
          onChange={(event) => {
            // A space is the sentinel that means « the box is open and empty ».
            // Emptying it entirely would close the box under the person's
            // fingers, which is a form fighting the person filling it in.
            onChange(event.target.value === "" ? " " : event.target.value);
          }}
          className={`${INPUT} mt-1.5`}
          placeholder={t("dealer.vehicules.autrePrecisez")}
          data-testid={`${id}-autre`}
        />
      ) : null}
    </div>
  );
}

export function VehicleFields({
  values,
  onChange,
  refusal,
  vocabulary,
  chosenEquipment,
  onToggleEquipment,
  mode,
  referenceLocked = false,
}: VehicleFieldsProps): React.JSX.Element {
  const { makes, options, equipment, cities, places } = vocabulary;
  const chosenMake = values["make"] ?? "";
  const modelsOfMake = makes.makes.find((make) => make.name === chosenMake)?.models ?? [];
  const choosesQuarter = places.length > 1;

  return (
    <>
      <div>
        <Field label={t("dealer.vehicules.reference")} htmlFor="vehicule-stockReference">
          <input
            id="vehicule-stockReference"
            value={values["stockReference"] ?? ""}
            readOnly={referenceLocked}
            onChange={(event) => {
              onChange("stockReference", event.target.value);
            }}
            className={`${INPUT} tabular-nums ${referenceLocked ? "bg-gray-50 text-gray-500" : ""}`}
            data-testid="vehicule-stockReference"
          />
        </Field>
        <p className="mt-1 text-[10px] text-gray-400">
          {referenceLocked ? t("dealer.vehicules.referenceFigee") : t("dealer.vehicules.referenceIndice")}
        </p>
      </div>

      {equipment.suggested.length === 0 ? null : (
        <div>
          <p className="text-xs font-medium text-gray-600">
            {t("dealer.vehicules.equipements")}{" "}
            <span className="font-normal text-gray-400">· {t("dealer.vehicules.equipementsFacultatif")}</span>
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="vehicule-equipements">
            {equipment.suggested.map((entry: string) => {
              const held = chosenEquipment.includes(entry);
              return (
                <button
                  key={entry}
                  type="button"
                  aria-pressed={held}
                  onClick={() => {
                    onToggleEquipment(entry);
                  }}
                  className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                    held
                      ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                      : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                  }`}
                  data-testid={`vehicule-equipement-${entry}`}
                >
                  {entry}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <ListWithOther
          id="vehicule-make"
          label={t("dealer.vehicules.marque")}
          value={chosenMake}
          options={makes.makes.map((make) => ({ key: make.name, label: make.name }))}
          otherKey={makes.otherMake}
          placeholder={t("dealer.vehicules.marqueChoisir")}
          onChange={(value) => {
            // The model depends on the make, so a new make clears it: a Corolla
            // under Nissan is a fiche that reads as a mistake to every buyer.
            onChange("make", value);
            onChange("model", "");
          }}
        />
        <ListWithOther
          id="vehicule-model"
          label={t("dealer.vehicules.modele")}
          value={values["model"] ?? ""}
          options={modelsOfMake.map((model) => ({ key: model, label: model }))}
          otherKey={makes.otherMake}
          placeholder={t("dealer.vehicules.modeleChoisir")}
          onChange={(value) => {
            onChange("model", value);
          }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        {FREE_FIELDS.map((champ) => (
          <Field key={champ.key} label={t(champ.cle)} htmlFor={`vehicule-${champ.key}`}>
            {/* `round F5`, to the pair: the drawing shows an EXAMPLE in every
                free field (`hakili-dealer.jsx`, « Ajouter un véhicule »), and
                an example is what tells a seller the shape expected , a year
                in four digits, a mileage without its unit. They are
                placeholders and never values: nothing here is submitted. */}
            <input
              id={`vehicule-${champ.key}`}
              {...(NUMERIC.has(champ.key) ? { inputMode: "numeric" as const, pattern: "[0-9]*" } : {})}
              placeholder={t(champ.exemple)}
              value={values[champ.key] ?? ""}
              onChange={(event) => {
                onChange(champ.key, event.target.value);
              }}
              className={INPUT}
              data-testid={`vehicule-${champ.key}`}
            />
          </Field>
        ))}
        {mode === "create" ? (
          <Field label={t("dealer.vehicules.prix")} htmlFor="vehicule-priceAmount">
            <input
              id="vehicule-priceAmount"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder={t("dealer.vehicules.prixExemple")}
              value={values["priceAmount"] ?? ""}
              onChange={(event) => {
                onChange("priceAmount", event.target.value);
              }}
              className={INPUT}
              data-testid="vehicule-priceAmount"
            />
          </Field>
        ) : null}
        {/* **The plate says it is optional, in the label.** The founder's own
            decision: in Mali some dealerships sell cars registered and some do
            not. A field that looks required and is not is a field a seller
            invents a value for, and an invented plate is indistinguishable from
            a real one in the index that refuses two live listings of one car. */}
        <Field
          label={`${t("dealer.vehicules.plaque")} · ${t("dealer.vehicules.facultatif")}`}
          htmlFor="vehicule-plate"
        >
          <input
            id="vehicule-plate"
            placeholder={t("dealer.vehicules.plaqueExemple")}
            value={values["plate"] ?? ""}
            onChange={(event) => {
              onChange("plate", event.target.value);
            }}
            className={INPUT}
            data-testid="vehicule-plate"
          />
        </Field>
      </div>

      {/**
       * `S4-3`: the declaration itself, asked once at listing and never
       * derived from `plate`. `create` only , editing it is not this round's,
       * exactly as the price above is `create`-only for its own reason.
       *
       * A blank selection is its own option and cannot be submitted: the door
       * refuses an unanswered declaration by name (`my-vehicles.controller.ts`),
       * and a select with no placeholder would silently default to one answer.
       */}
      {mode === "create" ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("dealer.vehicules.immatricule")} htmlFor="vehicule-registrationDeclared">
            <select
              id="vehicule-registrationDeclared"
              value={values["registrationDeclared"] ?? ""}
              onChange={(event) => {
                onChange("registrationDeclared", event.target.value);
                // Switching back to « Non » must not leave a stale number
                // behind it: the aggregate refuses a declared-unregistered car
                // that still carries one.
                if (event.target.value !== "oui") onChange("registrationNumber", "");
              }}
              className={INPUT}
              data-testid="vehicule-registrationDeclared"
            >
              <option value="">{t("dealer.vehicules.immatriculeChoisir")}</option>
              <option value="oui">{t("dealer.vehicules.oui")}</option>
              <option value="non">{t("dealer.vehicules.non")}</option>
            </select>
          </Field>
          {values["registrationDeclared"] === "oui" ? (
            <Field label={t("dealer.vehicules.numeroImmatriculation")} htmlFor="vehicule-registrationNumber">
              <input
                id="vehicule-registrationNumber"
                placeholder={t("dealer.vehicules.numeroImmatriculationExemple")}
                value={values["registrationNumber"] ?? ""}
                onChange={(event) => {
                  onChange("registrationNumber", event.target.value);
                }}
                className={INPUT}
                data-testid="vehicule-registrationNumber"
              />
            </Field>
          ) : null}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <Field label={t("dealer.vehicules.boite")} htmlFor="vehicule-gearbox">
          <select
            id="vehicule-gearbox"
            value={values["gearbox"] ?? ""}
            onChange={(event) => {
              onChange("gearbox", event.target.value);
            }}
            className={INPUT}
            data-testid="vehicule-gearbox"
          >
            <option value="">{t("dealer.vehicules.boiteChoisir")}</option>
            {options.gearboxes.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("dealer.vehicules.carburant")} htmlFor="vehicule-fuel">
          <select
            id="vehicule-fuel"
            value={values["fuel"] ?? ""}
            onChange={(event) => {
              onChange("fuel", event.target.value);
            }}
            className={INPUT}
            data-testid="vehicule-fuel"
          >
            <option value="">{t("dealer.vehicules.carburantChoisir")}</option>
            {options.fuels.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/* `R64`, rule one: the city is CHOSEN, never typed. A typed city is what
          let « Segou » and a Bamako quartier disagree and re-routed a
          dealership four hundred kilometres. */}
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("dealer.vehicules.ville")} htmlFor="vehicule-city">
          <select
            id="vehicule-city"
            value={values["city"] ?? ""}
            onChange={(event) => {
              /**
               * **The change that sets the city is the change that clears the
               * quartier.** It was the caller's job and both callers did it,
               * which is two implementations of one rule; here it cannot be
               * forgotten by a third form.
               */
              onChange("city", event.target.value);
              onChange("district", "");
            }}
            className={INPUT}
            data-testid="vehicule-city"
          >
            {cities.map((entry) => (
              <option key={entry.id} value={entry.name}>
                {entry.name}
              </option>
            ))}
          </select>
          {refusal?.field === "city" ? (
            <p className="mt-1 text-xs text-rose-600" data-testid="vehicule-refus-city">
              {refusal.message}
            </p>
          ) : null}
        </Field>
        <Field label={t("dealer.vehicules.quartier")} htmlFor="vehicule-district">
          {choosesQuarter ? (
            <select
              id="vehicule-district"
              value={values["district"] ?? ""}
              onChange={(event) => {
                onChange("district", event.target.value);
              }}
              className={INPUT}
              data-testid="vehicule-district"
            >
              <option value="">{t("dealer.vehicules.quartierChoisir")}</option>
              {places.map((place) => (
                <option key={place.code} value={place.label}>
                  {place.label}
                </option>
              ))}
            </select>
          ) : (
            // Outside Bamako the city has one zone and the quartier is the text
            // the dealer types , those cities have no quartier list to choose
            // from, and refusing free text there would refuse the address itself.
            <input
              id="vehicule-district"
              value={values["district"] ?? ""}
              onChange={(event) => {
                onChange("district", event.target.value);
              }}
              className={INPUT}
              data-testid="vehicule-district"
            />
          )}
          {refusal?.field === "district" ? (
            <p className="mt-1 text-xs text-rose-600" data-testid="vehicule-refus-district">
              {refusal.message}
            </p>
          ) : null}
        </Field>
      </div>
    </>
  );
}
